import type * as GeoJSONTypes from "geojson";
import { isAxiosError } from "axios";

import { EnhancedForestAPI } from "@/api/enhanced-forest";
import {
  wgs84PolygonGeometrySchema,
  wgs84PropertyGeometrySchema,
} from "@/api/geoJsonSchemas";
import type {
  DetailedForestStand,
  EnhancedVisualizationResponse,
} from "@/types/forest-detailed";
import { getSiteTypeLabel } from "@/utils/siteTypeLabels";

export interface PropertyForestContext {
  propertyId: string;
  bbox: [number, number, number, number] | null;
  standsGeoJson: GeoJSONTypes.FeatureCollection;
  propertyBoundaryGeoJson: GeoJSONTypes.GeoJsonObject | null;
  visualization: EnhancedVisualizationResponse;
}

function getStandSummaryForMap(stand: DetailedForestStand) {
  const exactMatch = (stand.time_series_data ?? []).find(
    (entry) =>
      entry.date === stand.data_date && entry.data_type === stand.data_type,
  );
  return (
    exactMatch?.stand_summary ??
    stand.time_series_data?.[0]?.stand_summary ??
    null
  );
}

type StandSummaryForMap = ReturnType<typeof getStandSummaryForMap>;

/** Identity + geometry-independent labelling for a stand map feature. */
type StandInventoryDates = EnhancedVisualizationResponse["stand_inventory_dates"][number];

function standIdentityProperties(
  stand: DetailedForestStand,
  propertyId: string,
  dates: StandInventoryDates | null,
) {
  const standNumber = stand.stand_number ?? null;
  return {
    stand_id: stand.stand_id,
    standId: stand.stand_id,
    stand_number: standNumber,
    STANDNUMBER: standNumber,
    species_code: stand.dominant_species_code ?? null,
    data_date: stand.data_date ?? stand.last_updated ?? null,
    // Provenance of the stand's tree data. Canonical values: 'metsakeskus',
    // 'field_scan', 'measurement' (legacy), 'manual'. Carried onto the map
    // feature so the Offer Decode seam can request the correct measurement basis
    // (modelled vs scanned) — without this the decode path is stuck on the
    // metsakeskus default and the scanned range is unreachable (yho5k.8).
    data_source: stand.data_source ?? null,
    inventory_observed_on: dates?.observed_on ?? null,
    inventory_growth_calculated_to: dates?.growth_calculated_to ?? null,
    property_id: propertyId,
  };
}

/** Physical measurements, preferring stand-level then time-series summary. */
function standMetricProperties(
  stand: DetailedForestStand,
  summary: StandSummaryForMap,
  uncertainty:
    | EnhancedVisualizationResponse["volume_uncertainty"]["stand_ranges"][number]
    | null,
) {
  const areaHectares = stand.area_hectares;
  const volumePerHectare = stand.total_volume ?? summary?.total_volume ?? null;
  const age = stand.mean_age ?? summary?.mean_age ?? null;
  return {
    area_hectares: areaHectares,
    area_sqm: areaHectares * 10000,
    age,
    average_age: age,
    ...standSummaryMetrics(summary),
    volume_per_hectare: volumePerHectare,
    volume_m3:
      volumePerHectare != null ? volumePerHectare * areaHectares : null,
    ...standUncertaintyMetrics(uncertainty),
  };
}

function standSummaryMetrics(summary: StandSummaryForMap) {
  return {
    average_height: summary?.mean_height ?? null,
    stems_per_hectare: summary?.total_stem_count ?? null,
    basal_area_per_hectare: summary?.total_basal_area ?? null,
  };
}

function standUncertaintyMetrics(
  uncertainty:
    | EnhancedVisualizationResponse["volume_uncertainty"]["stand_ranges"][number]
    | null,
) {
  return {
    volume_range_low_m3_per_ha: uncertainty?.lower_m3_per_ha ?? null,
    volume_range_high_m3_per_ha: uncertainty?.upper_m3_per_ha ?? null,
    volume_range_low_m3: uncertainty?.lower_m3 ?? null,
    volume_range_high_m3: uncertainty?.upper_m3 ?? null,
    volume_uncertainty_basis: uncertainty?.basis ?? null,
    volume_uncertainty_source: uncertainty?.source ?? null,
    volume_uncertainty_source_id: uncertaintySourceId(uncertainty),
    volume_uncertainty_provenance: uncertainty?.provenance ?? null,
  };
}

function uncertaintySourceId(
  uncertainty:
    | EnhancedVisualizationResponse["volume_uncertainty"]["stand_ranges"][number]
    | null,
) {
  return uncertainty?.source_id ?? null;
}

/** Site + development classification for a stand map feature. */
function standClassificationProperties(
  stand: DetailedForestStand,
  summary: StandSummaryForMap,
) {
  const fertilityClass = stand.site_characteristics?.fertility_class ?? null;
  return {
    fertility_class: fertilityClass,
    site_type: getSiteTypeLabel(fertilityClass) ?? null,
    development_class:
      stand.development_class ??
      stand.site_characteristics?.development_class ??
      summary?.development_class ??
      null,
    ...(typeof stand.cutting_maturity === "boolean"
      ? { cutting_maturity: stand.cutting_maturity }
      : {}),
  };
}

function toStandFeature(
  stand: DetailedForestStand,
  propertyId: string,
  uncertainty:
    | EnhancedVisualizationResponse["volume_uncertainty"]["stand_ranges"][number]
    | null,
  dates: StandInventoryDates | null,
): GeoJSONTypes.Feature | null {
  const geometry = wgs84PolygonGeometrySchema.safeParse(stand.geometry);
  if (!geometry.success) return null;

  const summary = getStandSummaryForMap(stand);

  return {
    type: "Feature",
    id: stand.stand_id,
    geometry: geometry.data,
    properties: {
      ...standIdentityProperties(stand, propertyId, dates),
      ...standMetricProperties(stand, summary, uncertainty),
      ...standClassificationProperties(stand, summary),
    },
  };
}

export function toStandFeatureCollection(
  stands: DetailedForestStand[],
  propertyId: string,
  volumeUncertainty: EnhancedVisualizationResponse["volume_uncertainty"] | undefined,
  standInventoryDates: readonly StandInventoryDates[],
): GeoJSONTypes.FeatureCollection {
  const uncertaintyByStand = new Map(
    (volumeUncertainty?.stand_ranges ?? []).map((range) => [range.stand_id, range]),
  );
  const datesByStand = new Map(standInventoryDates.map((dates) => [dates.stand_id, dates]));
  const features = stands
    .map((stand) =>
      toStandFeature(
        stand,
        propertyId,
        uncertaintyByStand.get(stand.stand_id) ?? null,
        datesByStand.get(stand.stand_id) ?? null,
      ),
    )
    .filter((feature): feature is GeoJSONTypes.Feature => feature != null);

  return {
    type: "FeatureCollection",
    features,
  };
}

function normalizeBoundaryGeometry(
  geometry: GeoJSONTypes.GeoJsonObject | null | undefined,
): GeoJSONTypes.GeoJsonObject | null {
  const parsed = wgs84PropertyGeometrySchema.safeParse(geometry);
  return parsed.success ? parsed.data : null;
}

/**
 * Reject a bbox whose values exceed the WGS84 ranges ([-180,180] / [-90,90]).
 *
 * The public property-overview boundary serves WGS84 bounds. Keep a strict
 * range check here so a wrong-CRS payload cannot send Leaflet off the property.
 */
export function normalizeWgs84Bbox(
  bbox: number[] | null | undefined,
): [number, number, number, number] | null {
  if (
    !Array.isArray(bbox) ||
    bbox.length !== 4 ||
    bbox.some((value) => !Number.isFinite(value))
  ) {
    return null;
  }
  const [minLng, minLat, maxLng, maxLat] = bbox;
  if (
    Math.abs(minLng) > 180 ||
    Math.abs(maxLng) > 180 ||
    Math.abs(minLat) > 90 ||
    Math.abs(maxLat) > 90 ||
    minLng >= maxLng ||
    minLat >= maxLat
  ) {
    return null;
  }
  return [minLng, minLat, maxLng, maxLat];
}

/** Gateway codes (`backend/app/core/error_translation.py`) for an id the cadastral register does not hold. */
const UNREGISTERED_PROPERTY_ERRORS = new Set(["property_not_found", "invalid_property_id"]);

export type PropertyForestContextFailure = "unregistered" | "failed";

/** Classify a failed forest-context read by the gateway's typed refusal. */
export function propertyForestContextFailure(
  error: unknown,
): PropertyForestContextFailure {
  if (!isAxiosError(error)) return "failed";
  const detail: unknown = error.response?.data?.detail;
  const code =
    detail && typeof detail === "object" && "error" in detail ? detail.error : null;
  return typeof code === "string" && UNREGISTERED_PROPERTY_ERRORS.has(code)
    ? "unregistered"
    : "failed";
}

export async function getPropertyForestContext(
  propertyId: string,
  bufferMeters: number = 50,
  signal?: AbortSignal,
  options?: { omitAuthorization?: boolean },
): Promise<PropertyForestContext> {
  const visualization = await EnhancedForestAPI.getDetailedVisualization(
    propertyId,
    bufferMeters,
    signal,
    options,
  );
  return buildPropertyForestContext(visualization);
}

export function buildPropertyForestContext(
  visualization: EnhancedVisualizationResponse,
): PropertyForestContext {
  const propertyId = visualization.property_id;
  const standsGeoJson = toStandFeatureCollection(
    visualization.detailed_stands ?? [],
    propertyId,
    visualization.volume_uncertainty,
    visualization.stand_inventory_dates,
  );
  return {
    propertyId,
    // The public boundary owns CRS normalization. Invalid coordinates remain
    // unavailable instead of being repaired through a second client path.
    bbox: normalizeWgs84Bbox(visualization.bbox),
    standsGeoJson,
    propertyBoundaryGeoJson: normalizeBoundaryGeometry(
      visualization.geometry as GeoJSONTypes.GeoJsonObject | null | undefined,
    ),
    visualization,
  };
}
